import React, { useContext, useEffect, useMemo, useState } from "react";
import { AuthContext } from "../AuthContext";
import { useNavigationSettings } from "../NavigationSettingsContext";
import { getOrderedNavigationItemsForSettings } from "../navigationConfig";
import { updateNavigationSettings } from "../api";

function createId() {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
        return crypto.randomUUID();
    }
    return `shortcut-${Math.random().toString(36).slice(2, 11)}`;
}

function normalizeShortcuts(shortcuts) {
    if (!Array.isArray(shortcuts)) {
        return [];
    }
    return shortcuts.map((shortcut) => ({
        id: shortcut.id || createId(),
        label: shortcut.label ?? "",
        icon: shortcut.icon ?? "",
        url: shortcut.url ?? "",
    }));
}

function SettingsPage() {
    const { token } = useContext(AuthContext);
    const { navigationItems, shortcuts, refresh, loading } = useNavigationSettings();
    const navigationDefinitions = useMemo(() => getOrderedNavigationItemsForSettings(), []);
    const [visibility, setVisibility] = useState(() => ({ ...navigationItems }));
    const [localShortcuts, setLocalShortcuts] = useState(() => normalizeShortcuts(shortcuts));
    const [saving, setSaving] = useState(false);
    const [successMessage, setSuccessMessage] = useState("");
    const [errorMessage, setErrorMessage] = useState("");

    useEffect(() => {
        setVisibility({ ...navigationItems });
    }, [navigationItems]);

    useEffect(() => {
        setLocalShortcuts(normalizeShortcuts(shortcuts));
    }, [shortcuts]);

    const toggleVisibility = (id) => {
        setVisibility((prev) => ({ ...prev, [id]: !prev[id] }));
    };

    const handleShortcutChange = (id, field, value) => {
        setLocalShortcuts((prev) =>
            prev.map((shortcut) => (shortcut.id === id ? { ...shortcut, [field]: value } : shortcut)),
        );
    };

    const handleAddShortcut = () => {
        setLocalShortcuts((prev) => [
            ...prev,
            { id: createId(), label: "", icon: "", url: "" },
        ]);
    };

    const handleRemoveShortcut = (id) => {
        setLocalShortcuts((prev) => prev.filter((shortcut) => shortcut.id !== id));
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (!token) {
            return;
        }
        setSaving(true);
        setSuccessMessage("");
        setErrorMessage("");
        try {
            const payload = {
                navigationItems: navigationDefinitions.reduce((acc, item) => {
                    acc[item.id] = visibility[item.id] !== false;
                    return acc;
                }, {}),
                shortcuts: localShortcuts.map((shortcut) => ({
                    id: shortcut.id,
                    label: shortcut.label?.trim() ?? "",
                    icon: shortcut.icon?.trim() ?? "",
                    url: shortcut.url?.trim() ?? "",
                })),
            };
            await updateNavigationSettings(token, payload);
            await refresh();
            setSuccessMessage("Einstellungen wurden erfolgreich gespeichert.");
        } catch (error) {
            console.error("Failed to save navigation settings", error);
            setErrorMessage("Einstellungen konnten nicht gespeichert werden. Bitte erneut versuchen.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="mx-auto max-w-4xl p-6">
            <h1 className="text-2xl font-semibold text-gray-800">Einstellungen</h1>
            <p className="mt-2 text-gray-600">
                Verwalten Sie hier, welche Navigationspunkte angezeigt werden und pflegen Sie die Shortcut-Leiste.
            </p>

            {loading && <p className="mt-4 text-sm text-gray-500">Lade aktuelle Einstellungen …</p>}
            {successMessage && <p className="mt-4 rounded bg-green-100 p-3 text-green-800">{successMessage}</p>}
            {errorMessage && <p className="mt-4 rounded bg-red-100 p-3 text-red-800">{errorMessage}</p>}

            <form className="mt-6 space-y-10" onSubmit={handleSubmit}>
                <section>
                    <h2 className="text-xl font-semibold text-gray-800">Navigation</h2>
                    <p className="mt-1 text-sm text-gray-600">
                        Deaktivierte Einträge werden in der Navigation für alle Benutzer ausgeblendet.
                    </p>
                    <div className="mt-4 divide-y divide-gray-200 overflow-hidden rounded-lg border border-gray-200 bg-white">
                        {navigationDefinitions.map((item) => (
                            <label
                                key={item.id}
                                className="flex items-center justify-between gap-4 px-4 py-3 hover:bg-gray-50"
                            >
                                <div>
                                    <p className="font-medium text-gray-900">{item.label}</p>
                                    <p className="text-sm text-gray-500">Pfad: {item.path}</p>
                                </div>
                                <input
                                    type="checkbox"
                                    className="h-5 w-5"
                                    checked={visibility[item.id] !== false}
                                    onChange={() => toggleVisibility(item.id)}
                                />
                            </label>
                        ))}
                    </div>
                </section>

                <section>
                    <div className="flex items-center justify-between">
                        <div>
                            <h2 className="text-xl font-semibold text-gray-800">Shortcut-Leiste</h2>
                            <p className="mt-1 text-sm text-gray-600">
                                Fügen Sie Quicklinks inklusive Icon hinzu. Links, die mit <code>/</code> beginnen, werden intern geöffnet,
                                alle anderen als externer Link.
                            </p>
                        </div>
                        <button
                            type="button"
                            onClick={handleAddShortcut}
                            className="rounded bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-500"
                        >
                            Shortcut hinzufügen
                        </button>
                    </div>

                    <div className="mt-4 space-y-4">
                        {localShortcuts.length === 0 && (
                            <p className="text-sm text-gray-500">Noch keine Shortcuts vorhanden.</p>
                        )}
                        {localShortcuts.map((shortcut) => (
                            <div key={shortcut.id} className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
                                <div className="grid gap-4 sm:grid-cols-3">
                                    <label className="flex flex-col text-sm font-medium text-gray-700">
                                        Bezeichnung
                                        <input
                                            type="text"
                                            className="mt-1 rounded border border-gray-300 px-2 py-1"
                                            value={shortcut.label}
                                            onChange={(event) => handleShortcutChange(shortcut.id, "label", event.target.value)}
                                            placeholder="z. B. Dashboard"
                                        />
                                    </label>
                                    <label className="flex flex-col text-sm font-medium text-gray-700">
                                        Icon (Emoji oder Text)
                                        <input
                                            type="text"
                                            className="mt-1 rounded border border-gray-300 px-2 py-1"
                                            value={shortcut.icon}
                                            onChange={(event) => handleShortcutChange(shortcut.id, "icon", event.target.value)}
                                            placeholder="🚗"
                                        />
                                    </label>
                                    <label className="flex flex-col text-sm font-medium text-gray-700">
                                        Link
                                        <input
                                            type="text"
                                            className="mt-1 rounded border border-gray-300 px-2 py-1"
                                            value={shortcut.url}
                                            onChange={(event) => handleShortcutChange(shortcut.id, "url", event.target.value)}
                                            placeholder="/work-orders"
                                        />
                                    </label>
                                </div>
                                <div className="mt-4 flex justify-end">
                                    <button
                                        type="button"
                                        onClick={() => handleRemoveShortcut(shortcut.id)}
                                        className="text-sm font-medium text-red-600 hover:text-red-500"
                                    >
                                        Entfernen
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>

                <div className="flex justify-end gap-3">
                    <button
                        type="submit"
                        className="rounded bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-500 disabled:cursor-not-allowed disabled:bg-blue-300"
                        disabled={saving}
                    >
                        {saving ? "Speichere …" : "Speichern"}
                    </button>
                </div>
            </form>
        </div>
    );
}

export default SettingsPage;
